/**
 * Onboarding Type Definitions
 * For white-glove customer onboarding workflow
 */

import { Timestamp } from 'firebase/firestore';

export type OnboardingStatus = 
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'completed'
  | 'cancelled';

export type OnboardingStep = 
  | 'kickoff'
  | 'gmail_setup'
  | 'property_import'
  | 'tenant_import'
  | 'training'
  | 'testing'
  | 'go_live'
  | 'follow_up';

export type StepStatus = 
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'skipped'
  | 'blocked';

export type ActivityType =
  | 'note'
  | 'call'
  | 'email'
  | 'meeting'
  | 'milestone'
  | 'blocker';

export type BlockerSeverity = 
  | 'low'
  | 'medium'
  | 'high'
  | 'critical';

export interface ChecklistItem {
  id: string;
  description: string;
  isRequired: boolean;
  isCompleted: boolean;
  completedAt?: Timestamp;
  completedBy?: string;
  completedByName?: string;
  notes?: string;
}

export interface OnboardingStepDetail {
  step: OnboardingStep;
  status: StepStatus;
  
  // Checklist items (vary by step)
  checklist: ChecklistItem[];
  
  // Timing
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  durationDays?: number;
  targetDate?: Timestamp;
  
  // Assignee (can differ from main assignedTo)
  assignedTo?: string;
  assignedToName?: string;
  
  // Notes specific to this step
  notes?: string;
  
  // Blockers
  isBlocked: boolean;
  blockerReason?: string;
  blockerResolvedAt?: Timestamp;
}

export interface OnboardingBlocker {
  id: string;
  step: OnboardingStep;
  reason: string;
  severity: BlockerSeverity;
  reportedAt: Timestamp;
  reportedBy: string;
  reportedByName?: string;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolvedByName?: string;
  resolution?: string;
}

export interface OnboardingActivity {
  id: string;
  type: ActivityType;
  subject: string;
  description?: string;
  performedBy: string;
  performedByName?: string;
  timestamp: Timestamp;
  metadata?: Record<string, any>;
}

export interface Onboarding {
  id: string;
  
  // Related entities
  leadId: string;
  customerId: string;
  contractId?: string;
  
  // Customer info (denormalized for quick access)
  customerName: string;
  customerEmail: string;
  companyName: string;
  doorCount: number;
  contractValue: number;
  
  // Overall status
  status: OnboardingStatus;
  currentStep: OnboardingStep;
  progress: number; // 0-100 (percentage complete)
  
  // Team
  assignedTo?: string;
  assignedToName?: string;
  
  // Timeline
  targetCompletionDate: Timestamp;
  kickoffDate?: Timestamp;
  goLiveDate?: Timestamp;
  completedDate?: Timestamp;
  
  // Steps with detailed tracking
  steps: OnboardingStepDetail[];
  
  // Customer satisfaction
  npsScore?: number;            // 0-10
  satisfactionRating?: number;  // 1-5 stars
  feedback?: string;
  
  // Metrics
  timeToValue?: number;         // Days from kickoff to go-live
  blockers: OnboardingBlocker[];
  
  // Notes and activities
  notes?: string;
  activities: OnboardingActivity[];
  
  // Audit
  createdAt: Timestamp;
  createdBy: string;
  createdByName?: string;
  updatedAt: Timestamp;
  updatedBy: string;
  updatedByName?: string;
}

export interface OnboardingStats {
  // Counts
  total: number;
  notStarted: number;
  inProgress: number;
  blocked: number;
  completed: number;
  cancelled: number;
  
  // Performance
  averageTimeToValue: number;      // Days from kickoff to go-live
  averageProgressPercentage: number;
  onTimeCompletionRate: number;    // % completed before target date
  
  // Customer satisfaction
  averageNPS: number;               // 0-10
  averageSatisfaction: number;      // 1-5 stars
  npsPromoters: number;             // Score 9-10
  npsPassives: number;              // Score 7-8
  npsDetractors: number;            // Score 0-6
  npsResponseRate: number;          // % with NPS score
  
  // Step breakdown
  stepStats: {
    [key in OnboardingStep]: {
      averageDuration: number;      // Days
      completionRate: number;       // %
      blockerCount: number;
    }
  };
  
  // By specialist
  bySpecialist: {
    [userId: string]: {
      name: string;
      activeCount: number;
      completedCount: number;
      averageNPS: number;
      averageTimeToValue: number;
    }
  };
  
  // Blockers
  activeBlockers: number;
  resolvedBlockers: number;
  blockersByStep: { [key in OnboardingStep]: number };
  blockersBySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
}

/**
 * Get default checklist for a specific onboarding step
 */
export function getDefaultChecklist(step: OnboardingStep): ChecklistItem[] {
  const checklists: Record<OnboardingStep, Omit<ChecklistItem, 'id' | 'isCompleted'>[]> = {
    kickoff: [
      { description: 'Welcome call scheduled', isRequired: true },
      { description: 'Customer expectations documented', isRequired: true },
      { description: 'Timeline agreed upon', isRequired: true },
      { description: 'Success criteria defined', isRequired: true },
      { description: 'Project plan shared', isRequired: false },
      { description: 'Contact information verified', isRequired: false },
    ],
    gmail_setup: [
      { description: 'Google Workspace account created', isRequired: true },
      { description: 'OAuth consent screen configured', isRequired: true },
      { description: 'Gmail API enabled', isRequired: true },
      { description: 'Test email sent', isRequired: true },
      { description: 'Test email received', isRequired: true },
      { description: 'Email signature configured', isRequired: false },
      { description: 'Forwarding rules set up (if needed)', isRequired: false },
    ],
    property_import: [
      { description: 'Property data template sent', isRequired: true },
      { description: 'Property data received', isRequired: true },
      { description: 'Properties imported', isRequired: true },
      { description: 'Units created', isRequired: true },
      { description: 'Addresses validated', isRequired: true },
      { description: 'Custom fields configured', isRequired: false },
      { description: 'Property images uploaded', isRequired: false },
    ],
    tenant_import: [
      { description: 'Tenant data template sent', isRequired: true },
      { description: 'Tenant data received', isRequired: true },
      { description: 'Tenants imported', isRequired: true },
      { description: 'Lease information added', isRequired: true },
      { description: 'Payment history imported', isRequired: false },
      { description: 'Tenant portal invites sent', isRequired: false },
      { description: 'Tenant portal access verified', isRequired: false },
    ],
    training: [
      { description: 'Training session scheduled', isRequired: true },
      { description: 'Admin training completed', isRequired: true },
      { description: 'Feature walkthrough done', isRequired: true },
      { description: 'Q&A session held', isRequired: true },
      { description: 'Training materials shared', isRequired: false },
      { description: 'Additional training scheduled (if needed)', isRequired: false },
    ],
    testing: [
      { description: 'Test tenant created', isRequired: true },
      { description: 'Test payment processed', isRequired: true },
      { description: 'Email notifications verified', isRequired: true },
      { description: 'Reports generated', isRequired: true },
      { description: 'Mobile access tested', isRequired: false },
      { description: 'Tenant portal tested', isRequired: false },
      { description: 'Edge cases validated', isRequired: false },
    ],
    go_live: [
      { description: 'Production data migrated', isRequired: true },
      { description: 'Go-live checklist completed', isRequired: true },
      { description: 'System activated', isRequired: true },
      { description: 'Customer notified', isRequired: true },
      { description: 'Support team briefed', isRequired: false },
      { description: 'Monitoring enabled', isRequired: false },
      { description: 'Backup verified', isRequired: false },
    ],
    follow_up: [
      { description: '7-day check-in call completed', isRequired: true },
      { description: 'Issues addressed', isRequired: false },
      { description: '30-day success review scheduled', isRequired: true },
      { description: 'NPS survey sent', isRequired: true },
      { description: 'Customer feedback collected', isRequired: false },
      { description: 'Hand-off to support completed', isRequired: true },
      { description: 'Success story documented (optional)', isRequired: false },
    ],
  };

  return checklists[step].map((item, index) => ({
    ...item,
    id: `${step}-${index}`,
    isCompleted: false,
  }));
}

/**
 * Get display name for onboarding step
 */
export function getStepDisplayName(step: OnboardingStep): string {
  const names: Record<OnboardingStep, string> = {
    kickoff: 'Kickoff Call',
    gmail_setup: 'Gmail Setup',
    property_import: 'Property Import',
    tenant_import: 'Tenant Import',
    training: 'Training Session',
    testing: 'Testing Phase',
    go_live: 'Go-Live',
    follow_up: 'Follow-Up',
  };
  return names[step];
}

/**
 * Get step order (for progression logic)
 */
export function getStepOrder(step: OnboardingStep): number {
  const order: Record<OnboardingStep, number> = {
    kickoff: 1,
    gmail_setup: 2,
    property_import: 3,
    tenant_import: 4,
    training: 5,
    testing: 6,
    go_live: 7,
    follow_up: 8,
  };
  return order[step];
}

/**
 * Get next step in sequence
 */
export function getNextStep(currentStep: OnboardingStep): OnboardingStep | null {
  const steps: OnboardingStep[] = [
    'kickoff',
    'gmail_setup',
    'property_import',
    'tenant_import',
    'training',
    'testing',
    'go_live',
    'follow_up',
  ];
  
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex === -1 || currentIndex === steps.length - 1) {
    return null;
  }
  
  return steps[currentIndex + 1];
}

/**
 * Get previous step in sequence
 */
export function getPreviousStep(currentStep: OnboardingStep): OnboardingStep | null {
  const steps: OnboardingStep[] = [
    'kickoff',
    'gmail_setup',
    'property_import',
    'tenant_import',
    'training',
    'testing',
    'go_live',
    'follow_up',
  ];
  
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex <= 0) {
    return null;
  }
  
  return steps[currentIndex - 1];
}

/**
 * Calculate progress percentage based on completed steps
 */
export function calculateProgress(steps: OnboardingStepDetail[]): number {
  if (steps.length === 0) return 0;
  
  const completedCount = steps.filter(s => s.status === 'completed').length;
  return Math.round((completedCount / steps.length) * 100);
}

/**
 * Calculate time-to-value (days from kickoff to go-live)
 */
export function calculateTimeToValue(
  kickoffDate?: Timestamp,
  goLiveDate?: Timestamp
): number | undefined {
  if (!kickoffDate || !goLiveDate) return undefined;
  
  const kickoff = kickoffDate.toDate();
  const goLive = goLiveDate.toDate();
  
  const diffTime = Math.abs(goLive.getTime() - kickoff.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Calculate step duration (days from start to completion)
 */
export function calculateStepDuration(
  startedAt?: Timestamp,
  completedAt?: Timestamp
): number | undefined {
  if (!startedAt || !completedAt) return undefined;
  
  const start = startedAt.toDate();
  const complete = completedAt.toDate();
  
  const diffTime = Math.abs(complete.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Determine NPS category
 */
export function getNPSCategory(score: number): 'promoter' | 'passive' | 'detractor' {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
}

/**
 * Check if onboarding is overdue
 */
export function isOverdue(targetCompletionDate: Timestamp, status: OnboardingStatus): boolean {
  if (status === 'completed' || status === 'cancelled') return false;
  
  const now = new Date();
  const target = targetCompletionDate.toDate();
  
  return now > target;
}

/**
 * Get color for step status
 */
export function getStepStatusColor(status: StepStatus): string {
  const colors: Record<StepStatus, string> = {
    pending: 'gray',
    in_progress: 'blue',
    completed: 'green',
    skipped: 'yellow',
    blocked: 'red',
  };
  return colors[status];
}

/**
 * Get color for blocker severity
 */
export function getBlockerSeverityColor(severity: BlockerSeverity): string {
  const colors: Record<BlockerSeverity, string> = {
    low: 'blue',
    medium: 'yellow',
    high: 'orange',
    critical: 'red',
  };
  return colors[severity];
}

/**
 * Initialize default onboarding steps
 */
export function initializeOnboardingSteps(): OnboardingStepDetail[] {
  const steps: OnboardingStep[] = [
    'kickoff',
    'gmail_setup',
    'property_import',
    'tenant_import',
    'training',
    'testing',
    'go_live',
    'follow_up',
  ];
  
  return steps.map(step => ({
    step,
    status: 'pending' as StepStatus,
    checklist: getDefaultChecklist(step),
    isBlocked: false,
  }));
}
